import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { styles } from './CashReportDocument.styles';
import {
  ITransactionForCashReportResponse,
  IMonthlyReportProps,
  IIncomesForCashResponse,
  IExpensesForCashResponse,
  IComparePeriodsResponse,
} from '../../../apis/models/transactions';
import { calculatePeriodDelta, zipPeriodTotalsByFeeType } from '../stats-report';

type TFunc = (key: string) => string;

interface ComparisonProps {
  data: IComparePeriodsResponse;
  labelA: string;
  labelB: string;
}

interface CashReportDocumentProps {
  cashReport: ITransactionForCashReportResponse | undefined;
  summaryData: IMonthlyReportProps | undefined;
  dateRange: { start: Date; end: Date };
  schoolName: string;
  resolveFeeType: (enumKey: string) => string;
  includeIncomes?: boolean;
  includeExpenses?: boolean;
  comparison?: ComparisonProps;
  t: TFunc;
}

function groupBy<T>(arr: T[], key: (item: T) => string): Map<string, T[]> {
  return arr.reduce((map, item) => {
    const k = key(item);
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(item);
    return map;
  }, new Map<string, T[]>());
}

const fmt$ = (n: number) => `$${n.toFixed(2)}`;

interface IncomeSectionProps {
  feeType: string;
  rows: IIncomesForCashResponse[];
  resolveFeeType: (key: string) => string;
  t: TFunc;
}

const IncomeSection: React.FC<IncomeSectionProps> = ({ feeType, rows, resolveFeeType, t }) => {
  const subtotal = rows.reduce((s, r) => s + r.fee_amount, 0);
  const label = resolveFeeType(feeType);
  return (
    <View style={styles.feeSection}>
      {/*
        Keep the section title + column headers locked together on the same page.
        If they don't fit, the whole anchor block moves to the next page.
      */}
      <View wrap={false}>
        <Text style={styles.feeSectionTitle}>{label}</Text>
        <View style={[styles.tableRow, styles.tableHeader]}>
          <Text style={styles.tableCell}>{t('tables.date')}</Text>
          <Text style={styles.tableCell}>{t('tables.names')}</Text>
          <Text style={styles.tableCell}>{t('tables.description')}</Text>
          <Text style={styles.tableCell}>{t('tables.employee_name')}</Text>
          <Text style={styles.tableCellAmount}>{t('tables.amount')}</Text>
        </View>
      </View>
      {/* Data rows flow freely across pages */}
      {rows.map((row, i) => (
        <View key={i} style={styles.tableRow}>
          <Text style={styles.tableCell}>{row.date_sold}</Text>
          <Text style={styles.tableCell}>{row.names}</Text>
          <Text style={styles.tableCell}>{row.description ?? ''}</Text>
          <Text style={styles.tableCell}>{row.cashier ?? ''}</Text>
          <Text style={styles.tableCellAmount}>{fmt$(row.fee_amount)}</Text>
        </View>
      ))}
      <View style={styles.subtotalRow}>
        <Text style={styles.subtotalLabel}>{t('tables.subtotal')}</Text>
        <Text style={styles.subtotalAmount}>{fmt$(subtotal)}</Text>
      </View>
    </View>
  );
};

interface ExpenseSectionProps {
  feeCode: string;
  rows: IExpensesForCashResponse[];
  resolveFeeType: (key: string) => string;
  t: TFunc;
}

const ExpenseSection: React.FC<ExpenseSectionProps> = ({ feeCode, rows, resolveFeeType, t }) => {
  const subtotal = rows.reduce((s, r) => s + r.fee_amount, 0);
  const label = resolveFeeType(feeCode);
  return (
    <View style={styles.feeSection}>
      <View wrap={false}>
        <Text style={styles.feeSectionTitle}>{label}</Text>
        <View style={[styles.tableRow, styles.tableHeader]}>
          <Text style={styles.tableCell}>{t('tables.date')}</Text>
          <Text style={styles.tableCell}>{t('tables.beneficiary_name')}</Text>
          <Text style={styles.tableCell}>{t('tables.description')}</Text>
          <Text style={styles.tableCell}>{t('tables.employee_name')}</Text>
          <Text style={styles.tableCell}>{t('tables.authoriser_name')}</Text>
          <Text style={styles.tableCellAmount}>{t('tables.amount')}</Text>
        </View>
      </View>
      {rows.map((row, i) => (
        <View key={i} style={styles.tableRow}>
          <Text style={styles.tableCell}>{row.date}</Text>
          <Text style={styles.tableCell}>{row.beneficiary_name}</Text>
          <Text style={styles.tableCell}>{row.description ?? ''}</Text>
          <Text style={styles.tableCell}>{row.cashier ?? ''}</Text>
          <Text style={styles.tableCell}>{row.authorizer ?? ''}</Text>
          <Text style={styles.tableCellAmount}>{fmt$(row.fee_amount)}</Text>
        </View>
      ))}
      <View style={styles.subtotalRow}>
        <Text style={styles.subtotalLabel}>{t('tables.subtotal')}</Text>
        <Text style={styles.subtotalAmount}>{fmt$(subtotal)}</Text>
      </View>
    </View>
  );
};

interface ComparisonSummaryCardProps {
  label: string;
  amountA: number;
  amountB: number;
  labelA: string;
  labelB: string;
}

const ComparisonSummaryCard: React.FC<ComparisonSummaryCardProps> = ({
  label,
  amountA,
  amountB,
  labelA,
  labelB,
}) => {
  const delta = calculatePeriodDelta(amountA, amountB);
  return (
    <View style={styles.summaryCard}>
      <Text style={styles.summaryLabel}>{label}</Text>
      <Text style={styles.summaryValue}>{fmt$(amountA)}</Text>
      <Text style={styles.comparisonMeta}>
        {labelA}: {fmt$(amountA)}
      </Text>
      <Text style={styles.comparisonMeta}>
        {labelB}: {fmt$(amountB)}
      </Text>
      {delta.deltaPercentage !== null && (
        <Text style={delta.isPositive ? styles.deltaPositive : styles.deltaNegative}>
          {delta.isPositive ? '+' : ''}
          {delta.deltaPercentage}%
        </Text>
      )}
    </View>
  );
};

interface ComparisonTableSectionProps {
  title: string;
  rows: ReturnType<typeof zipPeriodTotalsByFeeType>;
  resolveFeeType: (key: string) => string;
  labelA: string;
  labelB: string;
  /** Expenses read better inverted — a spend increase is the "bad" direction. */
  invertColor?: boolean;
  t: TFunc;
}

const ComparisonTableSection: React.FC<ComparisonTableSectionProps> = ({
  title,
  rows,
  resolveFeeType,
  labelA,
  labelB,
  invertColor = false,
  t,
}) => {
  if (rows.length === 0) return null;
  return (
    <View style={styles.feeSection}>
      <View wrap={false}>
        <Text style={styles.feeSectionTitle}>{title}</Text>
        <View style={[styles.tableRow, styles.tableHeader]}>
          <Text style={styles.tableCell}>{t('tables.fee_type')}</Text>
          <Text style={styles.tableCellAmount}>{labelA}</Text>
          <Text style={styles.tableCellAmount}>{labelB}</Text>
          <Text style={styles.tableCellAmount}>Δ%</Text>
        </View>
      </View>
      {rows.map((row) => {
        const isGood = invertColor ? !row.delta.isPositive : row.delta.isPositive;
        return (
          <View key={row.key} style={styles.tableRow}>
            <Text style={styles.tableCell}>{resolveFeeType(row.key)}</Text>
            <Text style={styles.tableCellAmount}>{fmt$(row.totalA)}</Text>
            <Text style={styles.tableCellAmount}>{fmt$(row.totalB)}</Text>
            <Text
              style={[
                styles.tableCellAmount,
                isGood ? styles.deltaPositive : styles.deltaNegative,
              ]}
            >
              {row.delta.deltaPercentage === null
                ? '—'
                : `${row.delta.isPositive ? '+' : ''}${row.delta.deltaPercentage}%`}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

interface ComparisonDailyTrendProps {
  comparison: ComparisonProps;
  t: TFunc;
}

// Fallback for the comparison chart: react-pdf can't render an ApexCharts canvas,
// so the daily trend is rendered as a plain table instead of blocking on rasterization.
const ComparisonDailyTrend: React.FC<ComparisonDailyTrendProps> = ({ comparison, t }) => {
  const { periodA, periodB } = comparison.data;
  const maxLen = Math.max(periodA.daily.length, periodB.daily.length);
  const days = Array.from({ length: maxLen }, (_, i) => i);

  return (
    <View style={styles.feeSection}>
      <View wrap={false}>
        <Text style={styles.feeSectionTitle}>{t('report.comparison_chart_title')}</Text>
        <View style={[styles.tableRow, styles.tableHeader]}>
          <Text style={styles.tableCell}>{t('report.day_label')}</Text>
          <Text style={styles.tableCellAmount}>{t('report.total_income')} ({comparison.labelA})</Text>
          <Text style={styles.tableCellAmount}>{t('report.total_expenses')} ({comparison.labelA})</Text>
          <Text style={styles.tableCellAmount}>{t('report.total_income')} ({comparison.labelB})</Text>
          <Text style={styles.tableCellAmount}>{t('report.total_expenses')} ({comparison.labelB})</Text>
        </View>
      </View>
      {days.map((dayIndex) => (
        <View key={dayIndex} style={styles.tableRow}>
          <Text style={styles.tableCell}>{dayIndex + 1}</Text>
          <Text style={styles.tableCellAmount}>{fmt$(periodA.daily[dayIndex]?.income ?? 0)}</Text>
          <Text style={styles.tableCellAmount}>{fmt$(periodA.daily[dayIndex]?.expense ?? 0)}</Text>
          <Text style={styles.tableCellAmount}>{fmt$(periodB.daily[dayIndex]?.income ?? 0)}</Text>
          <Text style={styles.tableCellAmount}>{fmt$(periodB.daily[dayIndex]?.expense ?? 0)}</Text>
        </View>
      ))}
    </View>
  );
};

const CashReportDocument: React.FC<CashReportDocumentProps> = ({
  cashReport,
  summaryData,
  dateRange,
  schoolName,
  resolveFeeType,
  includeIncomes = true,
  includeExpenses = true,
  comparison,
  t,
}) => {
  // Rows include a synthetic "TOTALS" entry whose periodTotal already sums every other
  // row — reducing over all rows would double-count it, so it's excluded here.
  const totalIncome =
    summaryData?.incomes
      .filter((item) => item.feeType !== 'TOTALS')
      .reduce((sum, item) => sum + item.periodTotal, 0) ?? 0;
  const totalExpenses =
    summaryData?.expenses
      .filter((item) => item.feeType !== 'TOTALS')
      .reduce((sum, item) => sum + item.periodTotal, 0) ?? 0;
  const netBalance = totalIncome - totalExpenses;

  const dateLabel = `${format(dateRange.start, 'dd/MM/yyyy')} - ${format(
    dateRange.end,
    'dd/MM/yyyy'
  )}`;

  const incomeGroups = cashReport
    ? groupBy(cashReport.incomes, (r) => r.feeType)
    : new Map<string, IIncomesForCashResponse[]>();

  const expenseGroups = cashReport
    ? groupBy(cashReport.expenses, (r) => r.feeCode)
    : new Map<string, IExpensesForCashResponse[]>();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>
            {comparison ? t('report.pdf_header_comparison') : t('report.pdf_header')}
          </Text>
          <Text style={styles.headerSubtitle}>{schoolName}</Text>
          <Text style={styles.headerSubtitle}>
            {comparison ? `${comparison.labelA}  vs.  ${comparison.labelB}` : dateLabel}
          </Text>
        </View>

        {/* Summary cards */}
        {comparison ? (
          <View style={styles.summaryRow}>
            <ComparisonSummaryCard
              label={t('report.total_income')}
              amountA={comparison.data.periodA.totalIncome}
              amountB={comparison.data.periodB.totalIncome}
              labelA={comparison.labelA}
              labelB={comparison.labelB}
            />
            <ComparisonSummaryCard
              label={t('report.total_expenses')}
              amountA={comparison.data.periodA.totalExpenses}
              amountB={comparison.data.periodB.totalExpenses}
              labelA={comparison.labelA}
              labelB={comparison.labelB}
            />
            <ComparisonSummaryCard
              label={t('report.net_balance')}
              amountA={comparison.data.periodA.netBalance}
              amountB={comparison.data.periodB.netBalance}
              labelA={comparison.labelA}
              labelB={comparison.labelB}
            />
          </View>
        ) : (
          <View style={styles.summaryRow}>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>{t('report.total_income')}</Text>
              <Text style={[styles.summaryValue, { color: '#2e7d32' }]}>{fmt$(totalIncome)}</Text>
            </View>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>{t('report.total_expenses')}</Text>
              <Text style={[styles.summaryValue, { color: '#c62828' }]}>
                {fmt$(totalExpenses)}
              </Text>
            </View>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>{t('report.net_balance')}</Text>
              <Text
                style={[styles.summaryValue, { color: netBalance >= 0 ? '#2e7d32' : '#c62828' }]}
              >
                {fmt$(netBalance)}
              </Text>
            </View>
          </View>
        )}

        {/* Period comparison breakdown + daily trend */}
        {comparison && (
          <View>
            <ComparisonTableSection
              title={t('reports_breakdown.incomes')}
              rows={zipPeriodTotalsByFeeType(
                comparison.data.periodA.incomes,
                comparison.data.periodB.incomes
              )}
              resolveFeeType={resolveFeeType}
              labelA={comparison.labelA}
              labelB={comparison.labelB}
              t={t}
            />
            <ComparisonTableSection
              title={t('reports_breakdown.expenses')}
              rows={zipPeriodTotalsByFeeType(
                comparison.data.periodA.expenses,
                comparison.data.periodB.expenses
              )}
              resolveFeeType={resolveFeeType}
              labelA={comparison.labelA}
              labelB={comparison.labelB}
              invertColor
              t={t}
            />
            <ComparisonDailyTrend comparison={comparison} t={t} />
          </View>
        )}

        {/* Income sections — skipped in comparison mode: the report is the comparison, not the raw ledger */}
        {!comparison && includeIncomes && cashReport && cashReport.incomes.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>{t('reports_breakdown.incomes')}</Text>
            {Array.from(incomeGroups.entries()).map(([feeType, rows]) => (
              <IncomeSection
                key={feeType}
                feeType={feeType}
                rows={rows}
                resolveFeeType={resolveFeeType}
                t={t}
              />
            ))}
          </View>
        )}

        {/* Expenses start on a new page when shown alongside incomes */}
        {!comparison && includeExpenses && cashReport && cashReport.expenses.length > 0 && (
          <View break={includeIncomes}>
            <Text style={styles.sectionTitle}>{t('reports_breakdown.expenses')}</Text>
            {Array.from(expenseGroups.entries()).map(([feeCode, rows]) => (
              <ExpenseSection
                key={feeCode}
                feeCode={feeCode}
                rows={rows}
                resolveFeeType={resolveFeeType}
                t={t}
              />
            ))}
          </View>
        )}
      </Page>
    </Document>
  );
};

export default CashReportDocument;
